/**
 * Dialog "Novo chat" (origem manual): Nome do contato*, Telefone, Cliente
 * (ClientPicker opcional), Departamento (mesa) e Mensagem inicial.
 */
import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input, Label, Select, Textarea } from '@/components/ui/input';
import { Dialog, Spinner } from '@/components/ui/misc';
import { cn } from '@/lib/utils';
import { ClientPicker, type ClientRef } from '@/features/tickets/ClientPicker';
import { desksListQuery } from '@/features/tickets/tickets-queries';
import { useCreateChat } from './chats-mutations';
import { errorMessage } from './chats-format';

export function NewChatDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (chatId: number) => void;
}) {
  const create = useCreateChat();
  const desksQ = useQuery(desksListQuery());
  const desks = (desksQ.data ?? []).filter((d) => d.active);

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [client, setClient] = useState<ClientRef | null>(null);
  const [deskId, setDeskId] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState<string | null>(null);

  const submit = () => {
    setError(null);
    if (!name.trim()) {
      setError('Informe o nome do contato.');
      return;
    }
    create.mutate(
      {
        requestorName: name.trim(),
        requestorPhone: phone.trim() || undefined,
        clientId: client?.id,
        deskId: deskId === '' ? undefined : Number(deskId),
        message: message.trim() || undefined,
      },
      {
        onSuccess: (chat) => onCreated(chat.id),
        onError: (e) => setError(errorMessage(e)),
      }
    );
  };

  return (
    <Dialog open onClose={onClose} title="Novo chat">
      <form
        className="flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <Field label="Nome do contato" htmlFor="nc-name" required>
          <Input
            id="nc-name"
            className="h-8"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Nome"
            autoFocus
          />
        </Field>

        <Field label="Telefone" htmlFor="nc-phone">
          <Input
            id="nc-phone"
            className="h-8"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="(00) 00000-0000"
          />
        </Field>

        <Field label="Cliente">
          <ClientPicker selected={client} onSelect={setClient} />
        </Field>

        <Field label="Departamento" htmlFor="nc-desk">
          <Select
            id="nc-desk"
            className="h-8"
            value={deskId}
            onChange={(e) => setDeskId(e.target.value)}
            disabled={desksQ.isPending}
          >
            <option value="">Sem departamento</option>
            {desks.map((d) => (
              <option key={d.id} value={d.id}>
                {d.displayName}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Mensagem inicial" htmlFor="nc-message">
          <Textarea
            id="nc-message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Escreva a primeira mensagem…"
            className="min-h-20"
          />
        </Field>

        {error ? (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        ) : null}

        <div className="mt-1 flex justify-end gap-2 border-t pt-3">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" size="sm" disabled={create.isPending}>
            {create.isPending ? <Spinner className="size-4 border-primary-foreground" /> : null}
            Iniciar conversa
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function Field({
  label,
  htmlFor,
  required,
  className,
  children,
}: {
  label: string;
  htmlFor?: string;
  required?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <Label htmlFor={htmlFor} className="text-[13px] font-semibold">
        {label}
        {required ? <span className="-ml-1 text-destructive">*</span> : null}
      </Label>
      {children}
    </div>
  );
}
